<template>
  <div class="permission-preview">
    <div class="permission-compare">
      <div
        class="permission-panel"
        data-cy="compare-before"
      >
        <div class="permission-panel-title">
          Before
        </div>
        <div
          v-for="option in options"
          :key="option.value"
          class="permission-item"
          :class="beforeClass(option.value)"
        >
          {{ option.text }}
        </div>
      </div>

      <div
        class="permission-panel"
        data-cy="compare-after"
      >
        <div class="permission-panel-title">
          After
        </div>
        <template v-for="option in options">
          <div
            v-if="has(after, option.value)"
            :key="option.value"
            class="permission-item"
            :class="afterClass(option.value)"
          >
            {{ option.text }}
          </div>
        </template>
        <div
          v-if="!after.length"
          class="permission-item permission-item-none"
        >
          No account access
        </div>
      </div>
    </div>

    <div
      class="permission-summary"
      data-cy="compare-summary"
    >
      <p
        v-if="!changed"
        class="mb-0 text-muted"
      >
        No changes. Permissions stay the same.
      </p>

      <div v-else>
        <ul class="permission-recap mb-2">
          <li
            v-if="has(after, 'billing')"
            class="text-success"
          >
            Use and manage credit cards, add funds and make manual payments
          </li>
          <li
            v-if="has(after, 'admin')"
            class="text-success"
          >
            Manage users, API keys and account security (includes Billing)
          </li>
          <li
            v-if="has(after, 'campaigns')"
            class="text-success"
          >
            <template v-if="isSellerAccount">
              Browse and apply for offers, manage approved offers and upload creatives
            </template>
            <template v-else>
              Manage campaigns, creatives and softphone calling
            </template>
          </li>
          <li v-if="has(after, 'call_logs')" class="text-success">
            View call logs, reporting and analytics
          </li>
        </ul>
        <ul class="permission-recap mb-0">
          <li
            v-if="!has(after, 'billing')"
            class="text-danger"
          >
            Cannot use credit cards, add funds or make manual payments
          </li>
          <li
            v-if="!has(after, 'admin')"
            class="text-danger"
          >
            Cannot manage users, API keys or account security
          </li>
          <li
            v-if="!has(after, 'campaigns')"
            class="text-danger"
          >
            <template v-if="isSellerAccount">
              Cannot browse or apply for offers, manage approved offers or upload creatives
            </template>
            <template v-else>
              Cannot manage campaigns, creatives or softphone calling
            </template>
          </li>
          <li v-if="!has(after, 'call_logs')" class="text-danger">
            Cannot view call logs, reporting or analytics
          </li>
        </ul>
      </div>
      <p class="mt-2 mb-0 text-muted">
        Personal password and MFA remain available. Only the selected access is granted; Account admin does not include <template v-if="isSellerAccount">offer access</template><template v-else>campaign access</template> or Call logs.
      </p>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'

export default {
  name: 'AccountPermissionPreview',

  props: {
    email: {
      type: String,
      default: ''
    },
    // Permissions the member has now, and the ones selected to save.
    before: {
      type: Array,
      default: () => []
    },
    after: {
      type: Array,
      default: () => []
    },
    // `{ value, text }` for each permission, in display order.
    options: {
      type: Array,
      default: () => []
    }
  },

  computed: {
    ...mapGetters('auth', ['isSellerAccount']),

    changed() {
      return this.options.some(option => this.gained(option.value) || this.lost(option.value))
    }
  },

  methods: {
    has(list, permission) {
      return list.includes(permission)
    },

    gained(permission) {
      return this.has(this.after, permission) && !this.has(this.before, permission)
    },

    lost(permission) {
      return this.has(this.before, permission) && !this.has(this.after, permission)
    },

    // Current: held and kept is plain, removed is red and struck through, not held is muted.
    beforeClass(permission) {
      return {
        'permission-item-lost': this.lost(permission),
        'permission-item-muted': !this.has(this.before, permission)
      }
    },

    // After: only what is held. Newly added is green.
    afterClass(permission) {
      return {
        'permission-item-gained': this.gained(permission)
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.permission-compare {
  display: flex;
  gap: 12px;
  margin-bottom: 16px;
}

.permission-panel,
.permission-summary {
  flex: 1;
  min-width: 0;
  padding: 12px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.03);
}

.permission-panel-title {
  margin-bottom: 8px;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.6);
}

.permission-item {
  padding: 6px 10px;
  margin-bottom: 6px;
  border-radius: 4px;
  font-size: 14px;
}

.permission-item-lost {
  color: #ff7b89;
  background: rgba(224, 82, 99, 0.18);
  text-decoration: line-through;
}

.permission-item-gained {
  color: #6fd39a;
  background: rgba(47, 179, 106, 0.18);
  font-weight: 600;
}

.permission-item-muted {
  color: rgba(255, 255, 255, 0.4);
}

.permission-item-none {
  color: rgba(255, 255, 255, 0.5);
  font-style: italic;
}

.permission-summary {
  margin-top: 4px;
}

.permission-summary ul {
  padding-left: 18px;
}
</style>
